import { UserPlus, X } from "lucide-react"

import type {
  GuildDirectoryMember,
  ManualParticipantMode,
  MartialArtPath,
  WeeklyAvailability,
} from "@/lib/guild-war-api"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "@/components/ui/combobox"
import { Label } from "@/components/ui/label"
import { LabeledSelect } from "@/components/ui/labeled-select"
import {
  MANUAL_MODE_OPTIONS,
  MANUAL_PRIMARY_PATH_OPTIONS,
  MANUAL_SECONDARY_PATH_OPTIONS,
  WEEKLY_AVAILABILITY_OPTIONS,
} from "@/features/guild-war/presentation/candidate-pool-filters"
import { toGuildWarPathPairDisplayLabel } from "@/lib/guild-war-path-display"

const formatMemberOptionLabel = (member: GuildDirectoryMember) => {
  return member.displayName ?? member.memberId
}

export function CandidatePoolManualAddCard({
  selectedMember,
  memberSearch,
  showMemberResults,
  memberSearchQuery,
  manualAddBlockedReason,
  manualMode,
  manualAvailability,
  manualPrimaryPath,
  manualSecondaryPath,
  overrideSavedPaths,
  selectedMemberPrimaryPath,
  selectedMemberSecondaryPath,
  shouldRequirePathInput,
  effectiveWeekId,
  addParticipantPending,
  manualActionMessage,
  addParticipantError,
  onClearSelectedMember,
  onMemberSearchFocus,
  onMemberSearchEscape,
  onMemberSearchChange,
  onSelectMember,
  onManualModeChange,
  onManualPrimaryPathChange,
  onManualSecondaryPathChange,
  onManualAvailabilityChange,
  onOverrideSavedPathsChange,
  onAddParticipant,
}: {
  selectedMember: GuildDirectoryMember | null
  memberSearch: string
  showMemberResults: boolean
  memberSearchQuery: {
    isLoading: boolean
    isError: boolean
    error: unknown
    data: GuildDirectoryMember[] | undefined
  }
  manualAddBlockedReason: string | null
  manualMode: ManualParticipantMode
  manualAvailability: WeeklyAvailability
  manualPrimaryPath: MartialArtPath | ""
  manualSecondaryPath: MartialArtPath | "none"
  overrideSavedPaths: boolean
  selectedMemberPrimaryPath: MartialArtPath | null
  selectedMemberSecondaryPath: MartialArtPath | null
  shouldRequirePathInput: boolean
  effectiveWeekId: string
  addParticipantPending: boolean
  manualActionMessage: string | null
  addParticipantError: boolean
  onClearSelectedMember: () => void
  onMemberSearchFocus: () => void
  onMemberSearchEscape: () => void
  onMemberSearchChange: (value: string) => void
  onSelectMember: (member: GuildDirectoryMember) => void
  onManualModeChange: (value: ManualParticipantMode) => void
  onManualPrimaryPathChange: (value: MartialArtPath | "") => void
  onManualSecondaryPathChange: (value: MartialArtPath | "none") => void
  onManualAvailabilityChange: (value: WeeklyAvailability) => void
  onOverrideSavedPathsChange: (value: boolean) => void
  onAddParticipant: () => void
}) {
  const hasSavedPrimaryPath = Boolean(selectedMemberPrimaryPath)
  const savedPathLabel = hasSavedPrimaryPath
    ? toGuildWarPathPairDisplayLabel(
        selectedMemberPrimaryPath,
        selectedMemberSecondaryPath,
        {
          noneLabel: "None",
        },
      )
    : "No saved path profile found"

  return (
    <Card className="border-primary/10 bg-[linear-gradient(180deg,color-mix(in_oklch,var(--card)_92%,white_8%),var(--card))] shadow-(--shadow-card)">
      <CardHeader className="gap-2 pb-3">
        <CardHeading icon={<UserPlus className="size-5" />}>
          Manually Add Participant
        </CardHeading>
        <CardDescription className="max-w-3xl text-sm">
          Search guild members by display name, then add them as a weekly or
          permanent participant without leaving this board.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 pt-0">
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1.35fr)_minmax(12rem,0.82fr)_minmax(12rem,0.82fr)_minmax(12rem,0.92fr)] xl:items-start">
          <div className="grid gap-1.5">
            <Label htmlFor="manual-member-search">Member</Label>
            <div className="relative">
              {selectedMember ? (
                <div className="flex h-11 items-center justify-between gap-2 rounded-xl border border-primary/20 bg-primary/5 px-3 text-sm">
                  <p className="min-w-0 truncate font-semibold text-foreground">
                    {formatMemberOptionLabel(selectedMember)}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 px-2.5"
                    onClick={onClearSelectedMember}
                  >
                    <X className="size-3.5" />
                    Change
                  </Button>
                </div>
              ) : (
                <Combobox
                  value={null}
                  inputValue={memberSearch}
                  items={memberSearchQuery.data ?? []}
                  open={showMemberResults}
                  filter={() => true}
                  onOpenChange={(nextOpen) => {
                    if (nextOpen) {
                      onMemberSearchFocus()
                    } else {
                      onMemberSearchEscape()
                    }
                  }}
                  onInputValueChange={(value) => {
                    onMemberSearchChange(value)
                  }}
                  onValueChange={(member) => {
                    if (!member) {
                      return
                    }

                    onSelectMember(member as GuildDirectoryMember)
                  }}
                >
                  <ComboboxInput
                    id="manual-member-search"
                    className="w-full"
                    placeholder="Search Discord display name"
                    showClear
                    onFocus={onMemberSearchFocus}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        onMemberSearchEscape()
                      }
                    }}
                  >
                    <ComboboxValue />
                  </ComboboxInput>
                  <ComboboxContent className="z-20">
                    <ComboboxList>
                      <ComboboxEmpty>
                        {memberSearchQuery.isLoading
                          ? "Searching guild members..."
                          : memberSearchQuery.isError
                            ? memberSearchQuery.error instanceof Error
                              ? memberSearchQuery.error.message
                              : "Failed to search guild members"
                            : "No matching guild members found."}
                      </ComboboxEmpty>
                      <ComboboxCollection>
                        {(member: GuildDirectoryMember) => (
                          <ComboboxItem key={member.memberId} value={member}>
                            <div className="font-semibold text-foreground">
                              {member.displayName ?? member.memberId}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {member.memberId}
                            </div>
                          </ComboboxItem>
                        )}
                      </ComboboxCollection>
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              )}
            </div>
            <div className="min-h-5">
              <p className="text-[11px] text-muted-foreground">
                {selectedMember
                  ? "Member selected. Use Change to pick someone else."
                  : "Search with 2+ characters. Searches current guild members only."}
              </p>
              {manualAddBlockedReason ? (
                <p className="mt-1 text-[11px] font-medium text-muted-foreground">
                  {manualAddBlockedReason}
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Participation Type</Label>
              <LabeledSelect
                value={manualMode}
                onValueChange={(value) =>
                  onManualModeChange(
                    (value as ManualParticipantMode | null) ?? "weekly",
                  )
                }
                options={MANUAL_MODE_OPTIONS}
                className="w-full"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Week</Label>
              <div className="flex h-11 items-center rounded-xl border border-border/60 bg-card/65 px-3.5 text-sm font-semibold">
                {effectiveWeekId || "Select a week below first"}
              </div>
            </div>

            {selectedMember ? (
              <div className="grid gap-1.5">
                <Label>Path Source</Label>
                <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-border/60 bg-card/65 px-3.5 text-sm">
                  <Checkbox
                    checked={overrideSavedPaths}
                    onCheckedChange={(checked) =>
                      onOverrideSavedPathsChange(Boolean(checked))
                    }
                  />
                  <span className="font-semibold">Override saved paths</span>
                </label>
              </div>
            ) : null}
          </div>

          <div className="grid gap-3">
            {!overrideSavedPaths && selectedMember ? (
              <div className="grid gap-1.5">
                <Label>Saved Paths</Label>
                <div className="flex min-h-11 items-center rounded-xl border border-border/60 bg-card/65 px-3.5 text-sm">
                  {savedPathLabel}
                </div>
              </div>
            ) : null}

            <div className="grid gap-1.5">
              <Label>Primary Path</Label>
              <LabeledSelect
                value={manualPrimaryPath}
                onValueChange={(value) =>
                  onManualPrimaryPathChange(
                    (value as MartialArtPath | null) ?? "",
                  )
                }
                options={MANUAL_PRIMARY_PATH_OPTIONS}
                className="w-full"
                placeholder="Select primary path"
                disabled={!shouldRequirePathInput}
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Secondary Path</Label>
              <LabeledSelect
                value={manualSecondaryPath}
                onValueChange={(value) =>
                  onManualSecondaryPathChange(
                    (value as MartialArtPath | "none" | null) ?? "none",
                  )
                }
                options={MANUAL_SECONDARY_PATH_OPTIONS}
                className="w-full"
                disabled={!shouldRequirePathInput}
              />
            </div>
            {!overrideSavedPaths && !hasSavedPrimaryPath && selectedMember ? (
              <p className="text-[11px] text-muted-foreground">
                No saved primary path exists. Choose paths to create the member
                profile.
              </p>
            ) : null}
          </div>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Availability</Label>
              <LabeledSelect
                value={manualAvailability}
                onValueChange={(value) =>
                  onManualAvailabilityChange(
                    (value as WeeklyAvailability | null) ?? "both",
                  )
                }
                options={WEEKLY_AVAILABILITY_OPTIONS}
                className="w-full"
                disabled={manualMode === "permanent"}
              />
            </div>

            <div className="grid gap-1.5">
              <Label className="invisible">Action</Label>
              <Button
                type="button"
                className="h-11 w-full"
                disabled={
                  !effectiveWeekId ||
                  !selectedMember ||
                  (shouldRequirePathInput && !manualPrimaryPath) ||
                  addParticipantPending
                }
                onClick={onAddParticipant}
              >
                {addParticipantPending
                  ? "Adding..."
                  : manualMode === "permanent"
                    ? "Add permanent member"
                    : "Add participant"}
              </Button>
            </div>
          </div>

          {manualActionMessage ? (
            <div className="xl:col-span-full">
              <StatusAlert
                tone={addParticipantError ? "danger" : "success"}
                intensity={addParticipantError ? "subtle" : undefined}
              >
                {manualActionMessage}
              </StatusAlert>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
